import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type {
  AlignItemsProps,
  DisplayProps,
  HeightProps,
  JustifyContentProps,
  MarginRightProps,
  MaxWidthProps,
  WidthProps,
} from 'styled-system';
import {
  alignItems,
  display,
  height,
  justifyContent,
  marginRight,
  maxWidth,
  width,
} from 'styled-system';

import type { FlexGrowProps, ObjectFitProps } from 'common/theme/system-utilities';
import { flexGrow, objectFit } from 'common/theme/system-utilities';
import { NavigationParent } from 'common/types/tracking';
import { SideNavType } from 'common/utils/navigation';
import { trackLogoClick } from '../../global-header/components/toolbar/tracking';
import TopNavigation from '../../global-header/components/top-navigation/TopNavigation';
import { LOGO_HEIGHT, LOGO_MAX_WIDTH } from './constants';
import type { ShopInShopNavigationProps } from './ShopInShopNavigation';
import { getMenuWidths, getTopBarNavigation } from './utils';

const Container = styled.div<
  WidthProps & DisplayProps & AlignItemsProps & JustifyContentProps & FlexGrowProps
>`
  ${width};
  ${display};
  ${alignItems};
  ${justifyContent};
  ${flexGrow};
`;

const getContainerStyles = (isMinimalHeader: boolean): ComponentProps<typeof Container> => ({
  width: [isMinimalHeader ? 'fit-content' : '100%', 'fit-content'],
  display: 'flex',
  alignItems: 'center',
  justifyContent: [isMinimalHeader ? 'flex-start' : 'space-between', 'flex-start'],
  flexGrow: 1,
});

const LogoWrapper = styled.a<MarginRightProps>`
  ${marginRight};
`;

const logoWrapperStyles = {
  mr: ['sp_16', 'sp_24', 'sp_0'],
};

const Logo = styled.img<
  MaxWidthProps & WidthProps & HeightProps & ObjectFitProps & MarginRightProps
>`
  ${maxWidth};
  ${width};
  ${height};
  ${objectFit};
  ${marginRight};
`;

const logoStyles: Partial<ComponentProps<typeof Logo>> = {
  maxWidth: LOGO_MAX_WIDTH,
  width: 'auto',
  height: LOGO_HEIGHT,
  objectFit: 'contain',
  mr: ['sp_16', 'sp_24', 'sp_0'],
};

const NavigationBarContainer = styled.div<MaxWidthProps & WidthProps>`
  ${maxWidth};
  ${width};
`;

const navigationBarContainerStyles: Partial<ComponentProps<typeof NavigationBarContainer>> = {
  maxWidth: ['fit-content', ...getMenuWidths()],
  width: ['fit-content', ...getMenuWidths()],
};

type MenuWithLogoProps = Pick<ShopInShopNavigationProps, 'logo' | 'navigation' | 'shopName'> & {
  isMinimalHeader?: boolean;
  shopUrl?: string;
};

const MenuWithLogo = ({
  logo,
  navigation,
  shopName,
  shopUrl,
  isMinimalHeader = false,
}: MenuWithLogoProps) => {
  const containerStyles = getContainerStyles(isMinimalHeader);
  const logoWrapperProps = shopUrl
    ? {
        href: shopUrl,
        onClick: () => {
          trackLogoClick(NavigationParent.ShopInShopNav, shopName);
        },
      }
    : ({ as: 'div' } as const);

  return (
    <Container {...containerStyles}>
      <LogoWrapper {...logoWrapperStyles} {...logoWrapperProps}>
        <Logo {...logoStyles} src={logo.url} alt={shopName || logo.alt || ''} />
      </LogoWrapper>
      <NavigationBarContainer {...navigationBarContainerStyles}>
        <TopNavigation
          navigation={getTopBarNavigation(navigation)}
          shopName={shopName}
          sideNavType={SideNavType.ShopInShop}
          withBoxShadow={false}
          withArrows
          wrapItems
          asButtonOnMobile
        />
      </NavigationBarContainer>
    </Container>
  );
};

export default MenuWithLogo;
